import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoRounded from '@mui/icons-material/InfoRounded';
import InvertedSlider from '../../../components/sliders/InvertedSlider';
import { formatHour } from '../../../utils/formatters';
import { useEffect, useMemo, useState } from 'react';
import OccupancyDaysCard from '@/features/portfolio-analysis/components/datagrid/OccupancyDaysCard';

const GRANULARITY = 4; //Represents how many sections pr hour the use can choose. 4 = 15 min steps.
const DEFAULT_START = 8;
const DEFAULT_END = 16;

const EditRowDataDialog = ({
  open,
  onClose,
  address = '',
  row,
  saveChanges,
}) => {
  const mapToSliderUnits = (days) => {
    if (!days) return {};
    const mapped = {};
    for (const [day, val] of Object.entries(days)) {
      mapped[day] = {
        ...val,
        start_time:
          typeof val.start_time === 'number'
            ? val.start_time * GRANULARITY
            : undefined,
        end_time:
          typeof val.end_time === 'number'
            ? val.end_time * GRANULARITY
            : undefined,
      };
    }
    return mapped;
  };

  const initialState = useMemo(() => {
    // Use manual if present, else calculated
    const source =
      row?.ManualOccupancy ?? row?.CalculatedOccupancy ?? row?.DefaultOccupancy;
    return mapToSliderUnits(source);
  }, [row]);
  const [state, setState] = useState(initialState);

  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  const isEdited = useMemo(() => {
    // If the state is the same as the initial state, return false
    if (JSON.stringify(state) === JSON.stringify(initialState)) {
      return false;
    }
    // If the state is different from the initial state, return true
    return true;
  }, [state, initialState]);

  const handleDelete = () => {
    saveChanges({ occupancy: null });
  };

  const mapToHours = (days) => {
    if (!days) return {};
    const mapped = {};
    for (const [day, val] of Object.entries(days)) {
      mapped[day] = {
        ...val,
        start_time:
          typeof val.start_time === 'number'
            ? val.start_time / GRANULARITY
            : undefined,
        end_time:
          typeof val.end_time === 'number'
            ? val.end_time / GRANULARITY
            : undefined,
      };
    }
    return mapped;
  };

  const handleSaveChanges = () => {
    if (JSON.stringify(row?.CalculatedOccupancy) === JSON.stringify(state)) {
      // If the state is the same as the calculated occupancy, set to null
      handleDelete();
      return;
    }

    const outgoing = mapToHours(state);
    saveChanges({ occupancy: outgoing });
  };

  // Handles changes for a specific day and field
  const handleDayChange = (day, field, value) => {
    setState((prevState) => ({
      ...prevState,
      [day]: {
        ...prevState[day],
        [field]: value,
      },
    }));
  };

  if (!initialState) {
    return null;
  }

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const dayLabels = {
    Monday: 'Mandag',
    Tuesday: 'Tirsdag',
    Wednesday: 'Onsdag',
    Thursday: 'Torsdag',
    Friday: 'Fredag',
    Saturday: 'Lørdag',
    Sunday: 'Søndag',
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Indstillinger for {address}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <ListItemText
            primary="Aktivitetsmønstre"
            secondary="Her kan du justere det aktivitetsmønster, der bruges i analysen – altså hvornår der forventes brug. Som standard foreslås et mønster baseret på ReMoni AI. Hvis ingen tydelige mønstre findes, bruges 08.00–16.00. Du kan altid ændre aktivets mønstret eller nemt gendanne ReMoni AI beregnet tider."
          />
          <Box>
            <Stack spacing={1}>
              {daysOfWeek.map((day) => (
                <Box
                  key={day}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <Box my={3} sx={{ minWidth: 90 }}>
                    {dayLabels[day]}
                  </Box>
                  <Button
                    variant={state[day]?.occupancy ? 'contained' : 'outlined'}
                    color={state[day]?.occupancy ? 'primary' : 'inherit'}
                    size="small"
                    onClick={() =>
                      handleDayChange(day, 'occupancy', !state[day]?.occupancy)
                    }
                  >
                    {state[day]?.occupancy ? 'Aktiv' : 'Inaktiv'}
                  </Button>
                  <Box sx={{ flex: 1 }}>
                    {state[day]?.occupancy && (
                      <InvertedSlider
                        isInverted={false}
                        min={0}
                        max={24 * GRANULARITY}
                        step={1}
                        disabled={!state[day]?.occupancy}
                        value={
                          state[day]?.occupancy &&
                          typeof state[day]?.start_time === 'number' &&
                          typeof state[day]?.end_time === 'number'
                            ? [state[day].start_time, state[day].end_time]
                            : [
                                DEFAULT_START * GRANULARITY,
                                DEFAULT_END * GRANULARITY,
                              ]
                        }
                        onChange={(_, newRange) => {
                          if (!Array.isArray(newRange)) return;
                          handleDayChange(day, 'start_time', newRange[0]);
                          handleDayChange(day, 'end_time', newRange[1]);
                        }}
                        valueLabelDisplay="on"
                        valueLabelFormat={(value) =>
                          formatHour(value / GRANULARITY)
                        }
                        marks={[
                          { value: 0, label: '0' },
                          { value: 4 * GRANULARITY, label: '4' },
                          { value: 6 * GRANULARITY, label: '6' },
                          { value: 8 * GRANULARITY, label: '8' },
                          { value: 10 * GRANULARITY, label: '10' },
                          { value: 12 * GRANULARITY, label: '12' },
                          { value: 14 * GRANULARITY, label: '14' },
                          { value: 16 * GRANULARITY, label: '16' },
                          { value: 18 * GRANULARITY, label: '18' },
                          { value: 20 * GRANULARITY, label: '20' },
                          { value: 24 * GRANULARITY, label: '24' },
                        ]}
                        disableSwap
                        sx={{
                          my: 2,
                          '& .MuiSlider-thumb': {
                            '&::after': {
                              content: '""',
                              display: 'none',
                            },
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
          {row?.ManualOccupancy && (
            <Tooltip title={'Perioden er blevet sat manuelt.'}>
              <InfoRounded />
            </Tooltip>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ textTransform: 'none' }} onClick={onClose}>
          Afbryd
        </Button>
        <Tooltip
          enterDelay={800}
          title={
            <>
              <Typography variant="body2">
                Det beregnede aktivitetsmønster er:
              </Typography>
              <OccupancyDaysCard daysOfWeek={row?.CalculatedOccupancy} />
            </>
          }
          placement="top"
        >
          <Button
            variant="outlined"
            onClick={() => setState(mapToSliderUnits(row?.CalculatedOccupancy))}
          >
            Gendan fra beregnet
          </Button>
        </Tooltip>
        <Button
          variant="contained"
          onClick={handleSaveChanges}
          disabled={!isEdited}
        >
          Gem ændringer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRowDataDialog;
